import type { Quarter, QuarterlyData } from './periods';
import { getForecastContext } from './periods';
import { useTableSettings } from './TableSettingsContext';
import { EditableCell } from './EditableCell';
import { ComputedCell } from './ComputedCell';
import { useCheckFlags } from './CheckFlagsContext';
import { RowLabel, type RowHint } from './RowHint';

interface EditableDataRowProps {
  label: string;
  data: QuarterlyData;
  onCellChange: (quarter: Quarter, value: number) => void;
  className?: string;
  /**
   * Let the HISTORICAL periods be edited too. Off by default, which keeps v1 exactly as it
   * was: this row is shared, and v1's history is entered once and then protected.
   *
   * v2 needs the opposite for an imported model, and needs it badly. A PDF import spans only
   * the years the filing reports, so EVERY period is historical and every cell was read-only -
   * a model whose figures are the thing most likely to be wrong, and not one of them could be
   * corrected. v2 passes the edit lock's state through `V2EditableRow`, so actuals are still
   * protected by default and can be unlocked deliberately.
   */
  allowHistorical?: boolean;
  /** Open a cell's editor on one click rather than two - see EditableCell. */
  editOnClick?: boolean;
}

export function EditableDataRow({
  label,
  data,
  onCellChange,
  className,
  allowHistorical = false,
  editOnClick = false,
}: EditableDataRowProps) {
  const { settings } = useTableSettings();
  const allQuarters = settings ? getForecastContext(settings).allQuarters : [];
  const historicalYears = new Set(settings?.historicalYears ?? []);

  const isHistoricalQuarter = (q: Quarter): boolean => {
    const parts = q.split('_');
    const yearStr = parts[parts.length - 1];
    const yr = parseInt(yearStr, 10);
    return !isNaN(yr) && historicalYears.has(yr);
  };

  return (
    <tr className={className}>
      <td className="sticky left-0 z-10 bg-white px-3 py-1.5 text-sm text-gray-700 border-r border-b border-gray-200 whitespace-nowrap">
        {label}
      </td>
      {allQuarters.map((q) => (
        <EditableCell
          key={q}
          value={data[q] ?? 0}
          quarter={q}
          onChange={(value) => onCellChange(q, value)}
          disabled={isHistoricalQuarter(q) && !allowHistorical}
          editOnClick={editOnClick}
        />
      ))}
    </tr>
  );
}

interface ComputedDataRowProps {
  label: string;
  /** The bubble's text, where the page's lookup by label does not have it; `null` for none. */
  hint?: RowHint | null;
  data: QuarterlyData;
  format?: 'currency' | 'percent' | 'number';
  className?: string;
}

export function ComputedDataRow({
  label,
  hint,
  data,
  format = 'currency',
  className,
}: ComputedDataRowProps) {
  const { settings } = useTableSettings();
  const allQuarters = settings ? getForecastContext(settings).allQuarters : [];
  const flagged = useCheckFlags().get(data);

  return (
    <tr className={className}>
      <td className="sticky left-0 z-10 bg-white px-3 py-1.5 text-sm text-gray-600 border-r border-b border-gray-200 whitespace-nowrap italic">
        <RowLabel label={label} hint={hint} />
      </td>
      {allQuarters.map((q) => (
        <ComputedCell
          key={q}
          value={data[q] ?? 0}
          quarter={q}
          format={format}
          flag={flagged?.get(q)}
        />
      ))}
    </tr>
  );
}
